import { ROLES } from '@habity/constants';
import type { NestedUser } from '@habity/types';

/**
 * Get user roles from the user object
 * Uses the roles array from user_roles relation
 */
const getUserRoles = (user?: NestedUser | null): string[] => {
  if (user?.roles && Array.isArray(user.roles)) {
    return user.roles;
  }
  return [];
};

/**
 * Check if a user has the admin role
 */
export const isAdmin = (user?: NestedUser | null) => {
  return getUserRoles(user).includes(ROLES.ADMIN);
};

/**
 * Check if a user has the broker role
 */
export const isBroker = (user?: NestedUser | null) => {
  return getUserRoles(user).includes(ROLES.BROKER);
};

/**
 * Check if a user has the user role
 */
export const isUser = (user?: NestedUser | null) => {
  return getUserRoles(user).includes(ROLES.USER);
};

// Convenient commonly used role combination
export const isBrokerOrAdmin = (user?: NestedUser | null) => {
  return isBroker(user) || isAdmin(user);
};

/**
 * Check if a user is viewing their own broker profile.
 * Matches by email — the broker role ensures it's actually a broker account.
 */
export const isOwnBrokerProfile = (
  user?: NestedUser | null,
  broker?: { email?: string | null } | null
) => {
  if (!user || !broker) return false;
  return isBroker(user) && !!user.email && user.email === broker.email;
};

/**
 * Get the display label for a user's role
 * Shows role name for admin/broker, otherwise "Beta"
 */
export const getRoleLabel = (user?: NestedUser | null): string => {
  if (isAdmin(user)) return 'Admin';
  if (isBroker(user)) return 'Mäklare';
  return 'Beta';
};
